import "../../../../Global/Base/Security/AESStreamCryptors";

import React from "react";
import ReactDOM from "react-dom";
import { debounce } from "lodash.throttle";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ICryptographicInfo } from "Global/Base/Crypto/ICryptographicInfo";
import { IMultiEditObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { CryptoInfoButton } from "UI/UI.React/components/CryptoInfoButton";
import { FileLink } from "UI/UI.React/components/FileLink";
import { SpinnerComponent } from "UI/UI.React/components/SpinnerComponent/SpinnerComponent";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { DigitalSignatureState, ISignatureInfo } from "../../../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../../../Global/Base/Crypto/ISignatureProvider";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { CancellationToken, CancellationTokenSource } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { IItemRightsCollection, RightType } from "../../../../Global/Base/Rights/RightTypes";
import { FileStreamReader } from "../../../../Global/Base/Streams/FileStreamReader";
import { IFileStreamReader } from "../../../../Global/Base/Streams/IFileStreamReader";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { FileUtils } from "../../../../Global/Base/Utils/FileUtils";
import { FunctionUtils } from "../../../../Global/Base/Utils/FunctionUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { EDMSException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { BasicDataType } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { OWDocumentDescription } from "../../../../OW.Remoting/Remoting/DataTypes/OWDocumentDescription";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader, UploadCancelledException } from "../../../../OW.Remoting/Remoting/FileUploader";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
import { SerializeFuncs } from "../../../../OW.Remoting/Remoting/Serializers";
import { IAwaitableEvent } from "../../../UI.BaseControls/AwaitableEvent";
import { IBussyAwaitable } from "../../../UI.BaseControls/ControlInterfaces";
import { IFileDownloadEvent } from "../../../UI.BaseControls/IEvent";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { FileEditElement } from "../../../UI.Controls/FileEditElement";
import { SimpleFileEditElement } from "../../../UI.Controls/SimpleFileEditElement";
import { UserActionLogger } from "../../DefaultInterface/UserActionLogger";
import { ComponentRightsExtractor } from "../../Rights/ComponentRightsExtractor";
import { Rights } from "../../Rights/Rights";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { FileEditProgressStatus, FileEditStatus, MenuItemFile } from "../FileMenu";
import { CryptoInfo, SignatureCssClass, SignatureValidationIntermediateMsg, SignatureValidationMsg } from "../Helpers/CryptoInfo";
import { FileEditCore, IFileEdit } from "../Helpers/FileEditCore";
import { MessageButtonType, MessageType, MessageWindow } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { UploadManager } from "../UploadManager";
import { ControlParams, IDigitalSignControl, IEditableControl, IHasFrameContext, IRequiresActualObject } from "./ControlInterfaces";
import { CultureDependentEditableControl } from "./CultureDependentEditableControl";
import { EditableControl } from "./EditableControl";
import { HasFrameContextMixin } from "./HasFrameContextMixin";

/**
 * Logovat potřebujeme i ve statických metodách. Proto musíme mít LazyLoadingSectionLogger.
 */
const logger = LazyLoadingSectionLogger.create(LoggingSection.fileEdit);

type SignatureInfoRequestParams = {
    callback?: (signatureInfo: ISignatureInfo, ct?: CancellationToken) => Promise<void>;
    ct?: CancellationToken;
};

export class FileEdit extends HasFrameContextMixin(CultureDependentEditableControl) implements IEditableControl, IRequiresActualObject, IDigitalSignControl, IBussyAwaitable, IHasFrameContext {
    public readonly uploadError: GlobalEvent<FileEdit, Error> = new GlobalEvent<FileEdit, Error>();
    public readonly fileUploaded: GlobalEvent<FileEdit, void> = new GlobalEvent<FileEdit, void>();

    private __fileName: string;
    private __fileSize: number;
    private __cryptoInfoTooltip: string;
    private __isSignatureLoading: boolean;

    //#region Fields
    protected _actualObject: OWObject;
    protected _fileEditElement: SimpleFileEditElement;
    protected _fileLinkElement: HTMLElement;
    protected _attributeData: OWDocumentDescription;
    protected _isSigningInProgress: boolean = false;
    protected _objectDataLoadingCTS?: CancellationTokenSource;
    private _archiveDocumentId: string;
    private _file: File; //drží referenci na právě uploadovaný soubor
    private _signatureProviderProfile: ISignatureProviderProfile | null | undefined = undefined;
    private _digitalSignatureState: DigitalSignatureState = DigitalSignatureState.empty;
    private _objectAttributesChangeListener: (sender: OWObject, attrs: string[]) => void;
    private readonly _allowSignWithoutEdit: boolean;
    private readonly _allowCounterSignature: boolean;
    private readonly _rightsDefinitions: IItemRightsCollection;
    private _signatureInfoDropDown: DropDownMenu = null;
    private _uploader: FileUploader;
    private _fileEditCore: FileEditCore;
    private _pendingTask: Promise<void>;
    private _showAsLink: boolean;
    private _isLinkMode: boolean;
    private _cryptoInfo: ICryptographicInfo;

    protected _useDigitalSign: boolean = false;
    //#endregion Fields

    private get _isSignatureLoading(): boolean {
        return this.__isSignatureLoading;
    }

    private set _isSignatureLoading(value: boolean) {
        this.__isSignatureLoading = value;
        this._renderLinkElement();
        this._setFileElementLoaderVisibility(value);
    }

    private get _cryptoInfoStartElement(): HTMLElement {
        return this._isLinkMode ? this._fileLinkElement : this._fileEditElement;
    }

    protected get _cryptoInfoTooltip(): string {
        return this.__cryptoInfoTooltip;
    }

    protected set _cryptoInfoTooltip(value: string) {
        this.__cryptoInfoTooltip = value;
        if (value.length > 0 && this._fileEditElement && this._fileEditElement instanceof FileEditElement) {
            this._fileEditElement.updateSignatureButtonTooltip(value);
        }
        this._renderLinkElement();
    }

    /**
     * Určuje, zda je komponenta uzamčená pro změnu názvu souboru
     */
    private get _isLocked(): boolean {
        return this._fileEditElement.isLocked;
    }
    private set _isLocked(uploading: boolean) {
        this._fileEditElement.isLocked = uploading;
    }
    protected get _fileName(): string {
        return this.__fileName;
    }

    private set _fileName(val: string) {
        this.__fileName = val;
        this._renderLinkElement();

        if (!this._fileEditElement)
            return;
        if ((val != null) && (this._fileEditElement.fileName === val))
            return;

        this._fileEditElement.fileName = val;
        this._clearSignatureProviderProfile();
        this._updateCanSign();
    }

    private get _fileSize(): number {
        return this.__fileSize;
    }

    private set _fileSize(value: number) {
        this.__fileSize = value;
        this._renderLinkElement();
    }

    private set _setSignatureProviderProfile(val: ISignatureProviderProfile | null | undefined) {
        this._signatureProviderProfile = val;
    }

    private set _signingInProgress(val: boolean) {
        this._isSigningInProgress = val;
    }

    public get useDigitalSign(): boolean {
        return this._useDigitalSign;
    }

    public set useDigitalSign(value: boolean) {
        this._useDigitalSign = value;
    }

    public get showAsLink(): boolean {
        return this._showAsLink;
    }

    public finishDelay: number;
    //#region Constructors
    /**
     * Vytvoří instanci
     * @param params Viz {@link ControlParams}.
     */
    constructor(params: ControlParams) {
        super(params);
        this._downloadFile = this._downloadFile.bind(this);

        const { element } = params;
        element.classList.add("FileEdit");
        this.attrName = element.getAttribute("data-attrname");

        this._allowSignWithoutEdit = this.getBoolDataAttr("allowsignwithoutedit");
        this._allowCounterSignature = this.getBoolDataAttr("allowcountersignature", true);
        this._showAsLink = this.getBoolDataAttr("showaslink", false);
        this._rightsDefinitions = ComponentRightsExtractor.loadRightsForTypes(this, RightType.useDigitalSign);

        this._createFileElement();
        if (this._showAsLink) {
            this._createFileLinkElement();
        }
        const mask = element.getAttribute("data-filemask");
        if (mask)
            this._fileEditElement.mask = mask;

        this.enabledChanged.add((_s, v) => {
            this._updateCanSign();
            this._updateDisplayMode();
            this._fileEditElement.enabled = v && v.value;
        });

        this.isEditingChanged.add((_s, _v) => {
            this._updateCanSign();
            this._updateDisplayMode();
        });

        this._updateDisplayMode();
        this._updateCanSign = debounce(this.__updateCanSign, 100, { leading: false, trailing: true });
        this._setListeners();
    }
    //#endregion Constructors

    //#region Methods
    protected _createFileElement(): void {
        //vytvoreni skryte komponenty file
        const fileEdit = new FileEditElement();
        if (this._captionLabel != null) { // pokud ma captionLabel propojíme s input elementem
            fileEdit.setInnerElementID(this._captionLabel.getAttribute("for"));
        }
        this._appendFileElement(fileEdit);
    }

    protected _appendFileElement(fileEdit: SimpleFileEditElement): void {
        fileEdit.id = UIManager.instance.generateElementID();
        this.innerElement = fileEdit;
        this._fileEditElement = fileEdit;
        this.element.appendChild(this.innerElement);
    }

    protected _createFileLinkElement(): void {
        this._fileLinkElement = createElement({ classes: "file-edit__link-element" });
        this.element.appendChild(this._fileLinkElement);
    }

    protected _setListeners(): void {
        if (!this._fileEditElement)
            return;

        const setInitialUploadState = (fileName: string) => {
            this._setStatus(FileEditStatus.uploading, 0);
            this._fileName = fileName;
        };

        this._fileEditElement.addEventListener("uploadRequestPreview", (event: IAwaitableEvent<boolean> & CustomEvent) => {
            event.enqueueResult(async (result: boolean) => {
                // pokud už někde dříve bylo výsledkem false, tak nemá cenu provádět další vyhodnocení
                if (result === false)
                    return false;

                if (!event.detail || !event.detail.files || event.detail.files.length === 0)
                    return false;

                // není povoleno nahrávat soubory nulové velikosti
                if (event.detail.files[0].size === 0) {
                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-539479")); // Nelze nahrát prázdný soubor.
                    event.detail.cancelled = true;
                    return false;
                }

                // Kontrola povolených přípon a velikosti souboru
                const streamReader = new FileStreamReader(event.detail.files[0]);

                setInitialUploadState(streamReader.name);

                if (!(await this._checkFileLimits(streamReader, event.detail.files[0]))) {
                    this._setStatus(FileEditStatus.empty);
                    event.detail.cancelled = true;
                    return false;
                }

                return true;
            });
        });

        // uživatel zvolil soubor k nahrání nebo kliknul na Opakovat
        this._fileEditElement.addEventListener("uploadRequest", async (event: CustomEvent) => {
            if (this.actualObject == null) // lepší by bylo zakázat kliknutí na file input
                return;

            this._clearFileInfo();
            let streamReader: IFileStreamReader;
            // nalezení souboru k nahrání
            if (!event.detail.retry) {
                // pokud uživatel vybral soubor k nahrání, tak se nahraje ten
                if (event.detail.files.length > 0) {
                    this._file = event.detail.files[0];
                    if (this._file == null)
                        return;

                    streamReader = new FileStreamReader(this._file);

                    setInitialUploadState(streamReader.name);
                }
            } else {
                // jinak se hledá poslední nahrávaný soubor na daném atributu dané instance -
                // při překlikávání mezi řádky seznamu už nemusí být poslední nahrávaný soubor dostupný přímo na komponentě
                const data = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
                if (data.uploader) {
                    if (data.uploader.fileStreamReader) {
                        streamReader = data.uploader.fileStreamReader;
                    } else {
                        //retry se může provést i když nebyl napřed zahájen upload na DMS (např. při podepsat a nahrát)
                        this._file = event.detail.files[0];
                        streamReader = new FileStreamReader(this._file);

                        setInitialUploadState(streamReader.name);

                        // Kontrola povolených přípon a velikosti souboru
                        if (!(await this._checkFileLimits(streamReader, this._file))) {
                            this._setStatus(FileEditStatus.empty);
                            return;
                        }
                    }
                }
            }

            if (streamReader == null)
                return;

            this.setValid();
            this.element.click();

            const attributeMetadata = await this._actualObject.getAttributeMetadata(this.attrName);

            if (attributeMetadata.basicType == BasicDataType.Binary) {
                // aktualizuje status fileEditu, čímž změní svoji podobu
                this._fileEditElement.setStatus(FileEditStatus.download);
                // nastaví binarni hodnotu dokumentu do binary atributu
                await this.actualObject.setValue(this.attrName, await streamReader.read());
            } else {
                this._fileEditCore.uploadFile(streamReader, event.detail.sign === true);
            }
        });

        // uživatel kliknul na zrušení uploadu
        this._fileEditElement.addEventListener("cancelRequest", () => {
            this._updateSignatureButton(null);
            const data = UploadManager.instance.getUploadData(this.actualObject.instanceID, this.attrName);
            if (data == null)
                return;

            // zpropaguje změnu i na další navázané komponenty
            data.uploader.cancel();

            this.setValid();
            this._updateCanSign();
        });

        this._fileEditElement.addEventListener("browseDialogOpened", () => {
            //v případě vyběru jiného souboru, se musí zahodit předchozí profil pro podepisování (může vybrat jinou příponu atp.)
            this._signatureProviderProfile = undefined;
        });

        this._fileEditElement.addEventListener("signatureInfoRequest", (event: CustomEvent) => this._handleSignatureInfoRequested(event));

        this._fileEditElement.addEventListener("showFileMenu", async () => {
            await this._updateCanSign(); // Před prvním otevřením menu je třeba vyhodnotit položky menu k podpisu
            this._setListenerMenu();
            await this._updateMetadata();
            if (this._fileName)
                await this._fileEditCore.updateDownloadUrl(false);
        });
    }

    protected _clearFileInfo(): void {
        this._updateSignatureButton(null);
        this._fileSize = null;
        this._fileEditCore?.clearDownloadUrl();
    }

    protected _handleSignatureInfoRequested(event: Event): void {
        if (!this._signatureInfoDropDown) {
            this._signatureInfoRequest({
                callback: (signatureInfo, ct) => this._showSignatureDropDown(event, signatureInfo, ct),
            });
        } else {
            this._signatureInfoDropDown.close();
            this._signatureInfoDropDown = null;
        }
    }

    private async _showSignatureDropDown(event: Event, signatureInfo: ISignatureInfo, ct?: CancellationToken): Promise<void> {
        if (signatureInfo) {
            if (!this._signatureInfoDropDown) {
                this._signatureInfoDropDown = await CryptoInfo.getCryptoInfoDropDown({ signatureInfo }, this._actualObject, this.attrName, ct);
                this._signatureInfoDropDown.show(this._cryptoInfoStartElement, event);
                this._signatureInfoDropDown.onClose.add(() => this._signatureInfoDropDown = null);
            } else {
                this._signatureInfoDropDown.close();
                this._signatureInfoDropDown = null;
            }
        }
    }

    protected _downloadFile(isArchiveDocument?: boolean): void {
        if (!this._isLocked) {
            this._fileEditCore.downloadFile(isArchiveDocument);
        }
    }

    private _setListenerMenu(): void {
        if (this._fileEditElement instanceof FileEditElement) {
            this._fileEditElement.fileMenu.registerEventListener("downloadRequest", (event: IFileDownloadEvent) => {
                this._downloadFile(event.detail ? event.detail.isArchiveDocument : false);
            });

            this._fileEditElement.fileMenu.registerEventListener("removeRequest", () => {
                const qm = new MessageWindow(new LocalizedStringBuilder("GUI-599180"));
                qm.okButtonCaption = "GUI-146838";
                qm.noButtonCaption = "GUI-146840";
                qm.type = MessageType.message;
                qm.show().then(async (r) => {
                    if (r.result == MessageButtonType.ok) {
                        this._updateSignatureButton(null);
                        this.element.click();
                        await this._removeFile();
                        this.valueChanged.trigger(this, undefined);
                    }
                });
            });

            this._fileEditElement.fileMenu.registerEventListener("signRequest", async () => {
                if (this.enabled && !this.isEditing)
                    this.clicked.trigger(this, {} as MouseEvent); // pokusí se přepnout detail do editace

                this._fileEditCore.createSignature(undefined, true);
            });

            this._fileEditElement.fileMenu.registerEventListener("copyUrl", () => this._fileEditCore.copyUrl());

            this._fileEditElement.fileMenu.registerEventListener("showVersions", async () => await this._fileEditCore.showArchiveVersions(this.element));
        }
    }

    /**
     * Pokud není vytvořen, tak vytvoří objekt s logikou pro práci se soubory a nastaví mu actual object.
     */
    private _setControlFile(actualObject: OWObject): void {
        if (!this._fileEditCore) {
            const fileEditAdapter: IFileEdit = {
                getFileName: () => this._fileName,
                getArchiveDocumentId: () => this._archiveDocumentId,
                getLocalFile: () => this._file,
                getSignatureProviderProfile: () => this._signatureProviderProfile,
                setSignatureProviderProfile: (value: ISignatureProviderProfile | null | undefined) => this._setSignatureProviderProfile = value,
                setSigningInProgress: (value: boolean) => this._signingInProgress = value,
                getControlValue: () => this.controlValue,
                getDigitalSignatureState: () => this._digitalSignatureState,
                updateCanSign: (param: any) => this._updateCanSign(param),
                setValid: () => this.setValid(),
                setInvalid: (locKey: string, message?: string) => this.setInvalid(locKey, message),
                setStatus: (status: FileEditStatus) => this._setStatus(status),
                changeProgressStatus: (type?: FileEditProgressStatus, value?: number) => this._changeProgressStatus(type, value),
                fileEditElement: this._fileEditElement,
            };
            this._fileEditCore = new FileEditCore(logger, fileEditAdapter, this.attrName, this, this._allowCounterSignature);
            this._fileEditCore.downloadUrlChanged.add(() => {
                this._renderLinkElement();
            });
        }
        this._fileEditCore.actualObject = actualObject;
    }

    private _setStatus(status: FileEditStatus, progressValue?: number): void {
        this._fileEditElement.setStatus(status);

        if (progressValue >= 0)
            this._changeProgressStatus(FileEditProgressStatus.progressValue, progressValue);
    }

    private _changeProgressStatus(type?: FileEditProgressStatus, value?: number): void {
        this._fileEditElement.changeProgressStatus(type, value);
    }

    private _signatureInfoRequest: (params: SignatureInfoRequestParams) => Promise<void> = FunctionUtils.debouncedFunctionAsync(async ({ callback, ct }: SignatureInfoRequestParams) => {
        try {
            if (this._actualObject && !(this._actualObject instanceof IMultiEditObject)) {
                this._showSignatureElementLoader();
                const response = await OWManager.getDocumentSignatureInfo(this._actualObject.instanceID, this.attrName, this._getRequestContext(ct));
                this._setSignatureState(response);
                this._updateSignatureButton({ signatureInfo: response });
                callback?.(response, ct);
                if (response != undefined) {
                    this._createTooltip(response);
                }
            }
        } catch (e) {
            //pokud jde o chybu DMS, zobrazí ji v message window
            this._fileEditCore.handleError(e, e instanceof EDMSException);
        } finally {
            this._hideSignatureElementLoader();
        }
    }, 300);

    private _showLoaderHandle: number | null = null;

    private _showSignatureElementLoader(): void {
        if (this._showLoaderHandle) {
            return;
        }
        this._showLoaderHandle = self.setTimeout(() => {
            this._isSignatureLoading = true;
            this._showLoaderHandle = null;
        }, 300);
    }

    private _hideSignatureElementLoader(): void {
        if (this._showLoaderHandle) {
            clearTimeout(this._showLoaderHandle);
        }
        this._showLoaderHandle = null;
        this._isSignatureLoading = false;
    }

    private _setFileElementLoaderVisibility(value: boolean): void {
        if (!(this._fileEditElement instanceof FileEditElement)) {
            return;
        }
        if (value) {
            this._fileEditElement.signatureElement.style.display = "none";
            this._fileEditElement.signatureElementLoader.showLoader();
            return;
        }
        this._fileEditElement.signatureElementLoader.hideLoader();
        this._fileEditElement.signatureElement.style.display = "";
    }

    /**
     * Nastaví stav podle získaných informací o podpisu ze služby
     * @param signatureInfo Informace o podpisu
     */
    private _setSignatureState(signatureInfo: ISignatureInfo): void {
        this._digitalSignatureState = FileEdit.getSignatureState(signatureInfo);
    }

    public static getSignatureState(signatureInfo: ISignatureInfo): DigitalSignatureState {
        if (signatureInfo == null) {
            return DigitalSignatureState.empty;
        } else if (signatureInfo.valid == null) {
            let retState: DigitalSignatureState;
            switch (signatureInfo.validationMessage) {
                case SignatureValidationMsg.AllowUnknownSignature:
                    retState = DigitalSignatureState.allowUnknown;
                    break;
                case SignatureValidationMsg.AttributeEncrypted:
                case SignatureValidationMsg.IsEncrypted:
                    retState = DigitalSignatureState.encrypted;
                    break;
                default:
                    retState = DigitalSignatureState.empty;
                    break;
            }
            if (EnumUtils.getEnumAsArray(SignatureValidationIntermediateMsg).includes(signatureInfo.validationMessage))
                return DigitalSignatureState.indeterminate;
            else
                return retState;
        } else {
            return signatureInfo.valid === true ? DigitalSignatureState.valid : DigitalSignatureState.invalid;
        }
    }

    private async _createTooltip(signatureInfo: ISignatureInfo): Promise<void> {
        this._cryptoInfoTooltip = await CryptoInfo.getCryptoInfoToolTip(signatureInfo);
    }

    public override validate(): boolean {
        if (this._uploader && !this._uploader.isError) { // Řeší souběh eventy valueChanged a zobrazení popup chyby. Eventa (validation manager) jinak odebere validační chyby i s tooltipem.
            this.setValid();
        }

        return this.isValid;
    }

    private async _removeFile(): Promise<void> {
        this._updateSignatureButton(null);
        await this._actualObject.setValue(this.attrName, null);
        this._attributeData = undefined;
        this._fileEditElement.fileName = "";
        this._clearSignatureProviderProfile();
        this._digitalSignatureState = DigitalSignatureState.empty;
        await this._updateCanSign();
    }

    private _updateCanSign: (ct?: any) => Promise<void>;

    private async __updateCanSign(_ct?: any): Promise<void> {
        if (this._fileEditElement instanceof FileEditElement) {
            const params: ICanSignParams = {
                actualObject: this.actualObject,
                attrName: this.attrName,
                control: this,
                digitalSignatureState: this._digitalSignatureState,
                isComponentEmpty: this._fileEditElement.status === FileEditStatus.empty,
                rightsCollection: this._rightsDefinitions,
                allowCounterSignature: this._allowCounterSignature,
                allowSignWithoutEdit: this._allowSignWithoutEdit,
                fileName: this._fileName,
            };

            const currentSigningEnabled = await FileEdit.checkSigningAvailable(params, this._getRequestContext());

            //Používá se pro možnost "Nahrát jiný a podepsat", pokud AllowCounterSignature=false zablokuje na FE podepisování, protože už je nahrán podepsaný soubor.
            let signingEnabledIfEmpty = currentSigningEnabled;
            if (params.digitalSignatureState != DigitalSignatureState.empty && this._allowCounterSignature === false) {
                params.digitalSignatureState = DigitalSignatureState.empty;
                params.fileName = "";
                params.isComponentEmpty = true;
                signingEnabledIfEmpty = await FileEdit.checkSigningAvailable(params, this._getRequestContext());
            }
            this._fileEditElement.updateSigningEnabled(currentSigningEnabled, signingEnabledIfEmpty);
        }
    }

    public static async checkSigningAvailable(params: ICanSignParams, requestContext?: RequestContext): Promise<boolean> {
        if (params.actualObject == null || !params.actualObject.canEdit || (params.control && !params.control.isValid)) {
            return false;
        }

        // zakázání tlačítka při probíhajícím uploadu (možná to v budoucnu budeme podporovat, ale zatím ne)
        const uploadData = UploadManager.instance.getUploadData(params.actualObject.instanceID, params.attrName);
        if (uploadData && uploadData.uploader && uploadData.uploader.isActive)
            return false;

        let signingEnabled: boolean = true;

        // Volba Vytvořit podpis má být povolena pouze za současného splnění:
        // ---------------------------------------------------------------------------------------
        // 1) Komponenta je editovatelná (Enabled) nebo je nastaveno AllowSignWithoutEdit
        if ((params.control && !params.control.enabled) && !params.allowSignWithoutEdit) {
            signingEnabled = false;

            // 2) Podpis dokumentu je prázdný nebo ((je platný nebo nerozhodnutelný) a je nastaveno AllowCounterSignature)
        } else if (params.digitalSignatureState !== DigitalSignatureState.empty && (!params.allowCounterSignature || (params.digitalSignatureState !== DigitalSignatureState.indeterminate && params.digitalSignatureState !== DigitalSignatureState.valid && params.digitalSignatureState !== DigitalSignatureState.allowUnknown && params.digitalSignatureState !== DigitalSignatureState.encrypted))) {
            signingEnabled = false;

            // 3) Vyhodnocení práv - UseDigitalSign a UseDigitalSignIf
        } else if (params.actualObject == null || !(await FileEdit._evaluateUseDigitalSign(params.rightsCollection, params.actualObject, requestContext))) {
            signingEnabled = false;

            // 4) Objekt a atribut je editovatelný na úrovni rozhraní serveru (není ReadOnly)
        } else if (params.actualObject == null || await params.actualObject.isAttributeReadOnly(params.attrName)) {
            signingEnabled = false;
            if (params.actualObject != null)
                logger.info(`Signing on component configured but attribute '${params.attrName}' of class '${params.actualObject.objectName}' is ReadOnly serverside - signing disabled.`); //`

            // 5) Pokud komponenta není ve stavu empty (s možností nahrát a podepsat), tak musí být dostupný podpisový provider, který umí vytvářet podpisy pro daný typ souboru
        } else if (params.actualObject == null || (!params.isComponentEmpty && await OWManager.getDigitalSignatureSettings(params.actualObject.instanceID, params.attrName,
            FileUtils.getFileExtension(params.fileName), requestContext) === null)) {
            signingEnabled = false;
            if (params.actualObject != null)
                logger.info(`Signing on component configured but there is no signing profile for class '${params.actualObject.objectName}', attribute '${params.attrName}' and file '${FileUtils.getFileExtension(params.fileName)}' - signing disabled.`); //`
        }

        return signingEnabled;
    }

    private static async _evaluateUseDigitalSign(rightsDefinitions: IItemRightsCollection, actualObject: OWObject, requestContext?: RequestContext): Promise<boolean> {
        if (!rightsDefinitions.useDigitalSign)
            return false;

        const rightResult = await Rights.evaluateSpecificControlRights(rightsDefinitions, rightsDefinitions.useDigitalSign, actualObject, undefined, undefined, requestContext);
        return rightResult.useDigitalSign;
    }

    private _clearSignatureProviderProfile(): void {
        this._signatureProviderProfile = undefined;
    }

    private _updateSignatureButton(cryptoInfo: ICryptographicInfo): void {
        this._cryptoInfo = cryptoInfo;
        this._renderLinkElement();
        if (this._fileEditElement instanceof FileEditElement) {
            const signatureInfo = cryptoInfo && cryptoInfo.signatureInfo;
            if (!this._fileEditElement)
                return;
            if (signatureInfo) {
                const validClasses: SignatureCssClass[] = [];
                if (CryptoInfo.isSigned(signatureInfo) || CryptoInfo.isEncrypted(cryptoInfo))
                    validClasses.push(SignatureCssClass.Signed);
                const warningClass = CryptoInfo.getSignatureWarningClass(signatureInfo);
                if (warningClass)
                    validClasses.push(warningClass as SignatureCssClass);
                this._fileEditElement.updateSignatureButton(validClasses);
            } else
                this._fileEditElement.updateSignatureButton([]);
        }
    }

    /**
     * Kontrola povolených přípon a velikosti souboru
     * @param streamReader Stream souboru
     * @param file Soubor
     */
    private async _checkFileLimits(streamReader: IFileStreamReader, file: File): Promise<boolean> {
        const fileLimitsInfo = await OWManager.getDMSFileLimits(this._getRequestContext());

        const fileName = file.name;

        const fileExtension = FileUtils.getFileExtension(fileName);

        const showDisallowedExtensionMessage = (...params: string[]) => {
            if (params && params[0])
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-680364", params), "GUI-167407"); // Soubory typu '@0' nejsou povoleny. | Chyba
            else
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-680411", params), "GUI-167407"); // Nahrávání souborů bez přípony není povoleno. | Chyba
        };

        const checkFileSize = (extension: string, streamReader: IFileStreamReader, sizeLimit: number): boolean => {
            const isSizeWithinLimit = (sizeLimit === -1 || streamReader.size <= sizeLimit);

            if (isSizeWithinLimit)
                return true;

            if (!sizeLimit) // == 0
                showDisallowedExtensionMessage(extension);
            else
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-680363", [extension, FileUtils.prettyPrintFileSize(sizeLimit, this.culture)]), "GUI-167407"); // Velikost nahrávaného souboru typu '@0' překračuje povolenou velikost @1. | Chyba

            return false;
        };

        const checkFileMask = async (fileExtension: string, streamReader: IFileStreamReader): Promise<boolean> => {
            for (const [extension, masks] of Object.entries(fileLimitsInfo.extensionMasks)) {
                if (StringUtils.compareCaseInsensitive(extension, fileExtension)) {
                    const array = [...new Uint8Array(await streamReader.read(0, 256))];

                    const fileHex = array.map(b => (b < 16 ? "0" : "") + b.toString(16).toUpperCase()).join("");
                    let isMaskMatched = false;
                    for (const mask of masks) {
                        const index = mask.indexOf("-");
                        const maskHexLength = mask.length - index - 1;
                        let start = index < 0 ? 0 : Number.parseInt(mask.charAt(index));

                        for (const bom of fileLimitsInfo.boms) {
                            if (fileHex.startsWith(bom)) {
                                if (fileHex.slice(start, start + maskHexLength).match(mask.slice(index + 1)))
                                    return true;

                                start += bom.length;
                                break;
                            }
                        }

                        if (fileHex.slice(start, start + maskHexLength).match(mask.slice(index + 1)))
                            return isMaskMatched = true;
                    }

                    if (!isMaskMatched)
                        MessageWindow.showAlert(new LocalizedStringBuilder("GUI-469050"), "GUI-167407"); // Soubor má nepovolenou příponu, nebo obsah souboru neodpovídá jeho příponě. | Chyba
                    return false;
                }
            }

            return true;
        };

        for (const [extensionsGroup, extensionsLimit] of Object.entries(fileLimitsInfo.fileLimits)) {
            const extensions = extensionsGroup.split(";");

            if (extensions.some(extension => StringUtils.compareCaseInsensitive(extension, fileExtension))) {
                // Zkontrolovat velikost
                if (!checkFileSize(fileExtension, streamReader, extensionsLimit))
                    return false;

                return await checkFileMask(fileExtension, streamReader);
            }
        }

        if (fileLimitsInfo.allowDefaultExtensions) {
            return checkFileSize(fileExtension, streamReader, (fileLimitsInfo.defaultExtensionsLimit != null ? fileLimitsInfo.defaultExtensionsLimit : 0))
                && await checkFileMask(fileExtension, streamReader);
        } else {
            showDisallowedExtensionMessage(fileExtension);
            return false;
        }
    }

    public override setInvalid(locKey: string, message: string = null): void {
        super.setInvalid(locKey, message);
    }

    /** Umožňuje čekat na běžící procesy na komponentě */
    public waitForPendingActions(): Promise<void> {
        return this._pendingTask ? this._pendingTask : Promise.resolve();
    }

    /** Umožňuje zrušit běžící procesy na komponentě */
    public cancelPendingActions(): void {
        this._pendingTask = null;
    }

    private _setUploadEvents(uploader: FileUploader): void {
        this._events.addWithKey("uploadEvents", uploader.onStart, async (sender: FileUploader) => {
            this._clearFileInfo();
            this._fileName = sender.fileName;
            this._isLocked = true; // při startu uploadu se komponenta zamkne
            // je potreba zavolat valueChange uz na zacatku, aby se dostal do rozeditovanych a pri preklikavani nenahral novy objekt
            this.valueChanged.trigger(this, undefined);
            this._clearSignatureProviderProfile();
            this._digitalSignatureState = DigitalSignatureState.empty;
            await this._updateCanSign();
        });

        this._events.addWithKey("uploadEvents", uploader.onProgress, (_sender: FileUploader, progress: number) => {
            UserActionLogger.logEvent(new Event("uploadProgress"));
            this._changeProgressStatus(FileEditProgressStatus.progressValue, progress);
        });

        this._events.addWithKey("uploadEvents", uploader.onError, async (_sender: FileUploader, ex: Error) => {
            await this._fileEditCore.handleError(ex);
            this.uploadError.trigger(this, ex);
        });

        this._events.addWithKey("uploadEvents", uploader.onFinished, async (_sender: FileUploader, documentId: string) => {
            this._changeProgressStatus(FileEditProgressStatus.complete);

            this._attributeData = SerializeFuncs.deserializeOWDocument(documentId, this._actualObject.instanceID, this.attrName);
            this.valueChanged.trigger(this, this._attributeData);

            // finalizacni sipka bude chvili zobrazena a až potom se umožní dál pracovat s komponentou
            if (uploader.finishDelay)
                await PromiseUtils.delay(uploader.finishDelay);
            this._isLocked = false;
            this.fileUploaded.trigger(this, null);
            this._setStatus(FileEditStatus.download);
            await this._updateMetadata();
            await this._signatureInfoRequest({});
            await this._updateCanSign();
            if (this._fileEditCore)
                await this._fileEditCore.updateDownloadUrl(false);
        });

        this._events.addWithKey("uploadEvents", uploader.onCancel, async (_sender: FileUploader, _exception: UploadCancelledException) => {
            this._updateSignatureButton(null);
            this.setValid();
            this._changeProgressStatus(FileEditProgressStatus.cancel);
            this._isLocked = false;
            await this._afterActualObjectLoad();
            this._clearSignatureProviderProfile();
            await this._signatureInfoRequest({});
            await this._updateCanSign();
        });
    }

    private _clearActualObjectUploadEvents(): void {
        this._events.removeByKey("uploadEvents");
    }

    /**
     * Metoda volana po změně actual objectu
     */
    private async _afterActualObjectLoad(): Promise<void> {
        // pokud je komponenta uzamčená (probíhá upload), tak není možné změnit její obsah
        if (this._isLocked)
            return;

        if (this.actualObject == null) {
            this._fileName = "";
            return;
        }

        const document = await this.actualObject.getValue(this.attrName);
        if (document == null || document === "") {
            this._fileName = "";
            return;
        }

        this._attributeData = document as OWDocumentDescription;
        try {
            let fileName = await this._attributeData.getName();
            if (fileName == null)
                fileName = "";

            this._fileName = fileName;
        } catch (e) {
            this._fileName = "";
        }
        this._fileEditElement.callEvent("signatureInfoRequest", {});
        if (this._isLinkMode) {
            await this._fileEditCore?.updateDownloadUrl(false);
        }
    }

    /** Aktualizuje informace z metadat */
    private async _updateMetadata(): Promise<void> {
        if (!this._attributeData)
            return;
        const metadata = await this._attributeData.getMetadata();

        // Načte informace o archivní verzi dokumentu, aby podle toho bylo možné zobrazit akce v menu
        this._archiveDocumentId = metadata.ArchiveDocumentID;
        this._fileSize = Number(metadata.FileSize ?? metadata.size);
        if (this._fileEditElement instanceof FileEditElement && this._fileEditElement.fileMenu) {
            const isActionEnabled = !StringUtils.isNullOrEmpty(this._archiveDocumentId);
            this._fileEditElement.fileMenu.setMenuItemEnabled(isActionEnabled, MenuItemFile.downloadArchiveVersion);
        }
    }

    //Aktualni objekt na detailu
    public set actualObject(value: OWObject) {
        this._clearFileInfo();
        this._setControlFile(value);
        if (this._isLinkMode) {
            this._fileEditCore?.updateDownloadUrl();
        }

        this._file = null;

        this._clearActualObjectUploadEvents();

        if (this._actualObject != null) {
            // při odchodu ze záznamu se komponenta odemkne a zvalidní
            this._isLocked = false;
            this.setValid();
            if (this._objectAttributesChangeListener) {
                this._actualObject.attributesChanged.remove(this._objectAttributesChangeListener);
                this._objectAttributesChangeListener = null;
            }
        }

        if (this._objectDataLoadingCTS)
            this._objectDataLoadingCTS.cancel();
        //pokud už existuje AO, tak odebereme existující listenery
        if (this._actualObject) {
            this._actualObject.attributesChanged?.remove(this._objectAttributesChangeListener);
        }
        this._actualObject = value;

        if (this._actualObject != null) {
            const cts = this._objectDataLoadingCTS = new CancellationTokenSource();
            this._actualObject.disposed.add(() => cts.cancel());

            // při příchodu na záznam se zaregistruje naslouchání uploadu (na stávajícím uploaderu nebo se nový zaregistruje) a nastaví se podle toho stav a obsah komponenty
            let uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
            if (!uploaderData)
                uploaderData = UploadManager.instance.registerUpload(this._actualObject.instanceID, this.attrName, undefined, this._getRequestContext());
            if (this.finishDelay)
                uploaderData.uploader.finishDelay = this.finishDelay;
            const uploader = uploaderData.uploader;
            this._uploader = uploader;
            this._setUploadEvents(uploader);

            if (uploader.isStarted) {
                this._fileName = uploader.fileName;

                // pokud už probíhá upload, tak se ihned nastaví správná hodnota progresu
                if (!uploader.isComplete && !uploader.isError)
                    this._changeProgressStatus(FileEditProgressStatus.progressValue, uploader.currentProgress);
            }
            if (uploader.isError) {
                this.setInvalid(null, uploaderData.lastError);
                this._setStatus(FileEditStatus.error);
            }

            this._isLocked = uploader.isActive;

            this._actualObject.attributesChanged.add(this._objectAttributesChangeListener = async (obj, attributeNames) => {
                try {
                    await this._updateCanSign(this._objectDataLoadingCTS.token);

                    if (this._isSigningInProgress || attributeNames.find(attributeName => !StringUtils.compareCaseInsensitive(this.attrName, attributeName))) {
                        return;
                    }

                    const document = await obj.getValue(this.attrName);
                    if (document != null) { // aktualizace informace pouze pokud je atribut vyplnen
                        if (!this._uploader.isActive) {
                            await this._signatureInfoRequest({ ct: this._objectDataLoadingCTS.token });
                            await this._updateCanSign(this._objectDataLoadingCTS.token);
                        } else {
                            this._updateSignatureButton(null);
                        }
                    }
                } catch (e) {
                    PromiseUtils.cancellationCatch(e);
                }
            });

            this._setFileEditActionsAvailability();

            // při změně atributu je potřeba stáhnout informace o podpisu a znova vyhodnotit případnou podmínku UseDigitalSignIf a ostatní pravidla
            this._signatureInfoRequest({ ct: this._objectDataLoadingCTS.token }).then(() => this._updateCanSign(this._objectDataLoadingCTS.token)).catch(PromiseUtils.cancellationCatch);
        } else {
            this._afterActualObjectLoad();
        }
    }

    //Aktualni objekt na detailu - master objektu na LOV
    public get actualObject(): OWObject {
        return this._actualObject;
    }

    // Nastavi hodnotu editu
    public override set controlValue(value: any) {
        if (this._isLocked || !this.canValueLoad)
            return;

        this._clearFileInfo();

        if (!value) {
            this._fileName = "";
            this._attributeData = null;
            this._archiveDocumentId = null;
            return;
        }

        if (!(value instanceof OWDocumentDescription))
            return;

        this._attributeData = value;

        this._pendingTask = this._attributeData.getName().then((n) => {
            this._fileName = n;
        }).catch((err) => {
            logger.warn(err);
        });

        this._updateMetadata();

        this._signatureInfoRequest({ ct: this._objectDataLoadingCTS.token }).then(() => this._updateCanSign(this._objectDataLoadingCTS.token)).catch(PromiseUtils.cancellationCatch);
    }

    public override get controlValue(): any {
        return this._attributeData;
    }

    public override get displayedValue(): ITextBuilder {
        return this._fileEditElement.fileName;
    }

    public override set displayedValue(v: ITextBuilder) {
        this._fileName = v?.toString();
    }

    private _setFileEditActionsAvailability(): void {
        this._actualObject.getAttributeMetadata(this.attrName).then(attributeMetadata => {
            if (this._fileEditElement instanceof FileEditElement && attributeMetadata?.basicType === BasicDataType.Binary) {
                this._fileEditElement.enableFileMenuDownload = false;
                this._fileEditElement.enableFileMenuDownloadArchiveActions = false;
                this._fileEditElement.enableFileMenuShowVersions = false;
                this._fileEditElement.enableFileMenuCopyUrl = false;
            }
        });
    }

    private _updateDisplayMode(): void {
        if (this.showAsLink && !(this.enabled && this.isEditing)) {
            this._showAsLinkMode();
            return;
        }
        this._showAsEditMode();
    }

    private _showAsEditMode(): void {
        if (!this._isLinkMode) {
            return;
        }
        this._isLinkMode = false;
        this._unmountLinkElement();
        this.element.classList.remove("file-edit--link");
        this.element.classList.add("EditableControl", "file-edit--edit");
        this._fileEditElement.visible = true;
        this._fileLinkElement.classList.add("file-edit__link-element--hidden");
    }

    private _showAsLinkMode(): void {
        if (this._isLinkMode) {
            return;
        }
        this._isLinkMode = true;
        this._fileEditElement.visible = false;
        this.element.classList.remove("EditableControl", "file-edit--edit");
        this.element.classList.add("file-edit--link");
        this._fileLinkElement.classList.remove("file-edit__link-element--hidden");
        this._fileEditCore?.updateDownloadUrl(false);
        this._renderLinkElement();
    }

    private _renderLinkElement(): void {
        if (!this._isLinkMode || !this._fileLinkElement) {
            return;
        }
        const isSigned = CryptoInfo.isSigned(this._cryptoInfo?.signatureInfo) || CryptoInfo.isEncrypted(this._cryptoInfo);
        ReactDOM.render(
            <FileLink
                fileName={this._fileName}
                fileDownloadLink={this._fileEditCore?.downloadUrl}
                fileSize={this._fileSize}
                onDownloadRequested={this._downloadFile}
                signatureElement={
                    this._isSignatureLoading ? <SpinnerComponent /> :
                        isSigned ?
                            <CryptoInfoButton
                                title={this._cryptoInfoTooltip}
                                onClick={event => this._handleSignatureInfoRequested(event)}
                                cryptoInfo={this._cryptoInfo} /> :
                            null
                } />,
            this._fileLinkElement,
        );
    }

    private _unmountLinkElement(): void {
        if (this._fileLinkElement) {
            ReactDOM.unmountComponentAtNode(this._fileLinkElement);
        }
    }

    public override dispose(): void {
        if (this._actualObject && this._objectAttributesChangeListener) {
            this._actualObject.attributesChanged.remove(this._objectAttributesChangeListener);
            this._objectAttributesChangeListener = null;
        }

        this._objectDataLoadingCTS?.cancel();
        this._clearActualObjectUploadEvents();
        if (this._actualObject != null) {
            this._actualObject = null;
        }

        this._unmountLinkElement();
        super.dispose();
    }

    /*
    private _testEnvelopingSignature() {
        // Enveloping Test

        const xmlData = `<gu:ProtectedDocuments xmlns:gu="http://mmr.cz/wcc/v1.0.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" format="MMR-XML" version="1.0" xsi:schemaLocation="http://mmr.cz/wcc/v1.0.0 FormatDefinition_v1.0.xsd">
            <ProtectedFile>
            <Id>3076979</Id>
            <AltId>46KIXW_W6UATG1UTFKNK_W</AltId>
            <Revison>3</Revison>
            <FileName>Tisková verze žádosti fpyrVP-0003.pdf</FileName>
            <MimeType>application/pdf</MimeType>
            <Size>306274</Size>
            <DigestType>SHA-512</DigestType>
            <DigestValue>2YBDH3CiaYKnW5utwdxbYDf0wqZg3xIRMlj5+MdrJoDCruDy5PM2p6bJ+wDO+fq8SgjaRtCtQVRue3NVw/c/TQ==</DigestValue>
            </ProtectedFile>
        </gu:ProtectedDocuments>`;

        const xmlData = `<ds:Signature xmlns:ds="http://www.w3.org/2000/09/xmldsig#" Id="Signature-874039591">
                <ds:SignedInfo>
                    <ds:CanonicalizationMethod Algorithm="http://www.w3.org/2001/10/xml-exc-c14n#"/>
                    <ds:SignatureMethod Algorithm="http://www.w3.org/2001/04/xmldsig-more#rsa-sha512"/>
                    <ds:Reference Id="envObjRef-3742974204" URI="#envObj-2720751675">
                        <ds:DigestMethod Algorithm="http://www.w3.org/2001/04/xmlenc#sha512"/>
                        <ds:DigestValue>/zrtNxe374zXDI0iF85UkOC4HZobCBJM4Euys9zf+j13AHFGU4AzeF2Sjp6WqZ0+kDoG1DkCdHn+QGJKdYbFLA==</ds:DigestValue>
                    </ds:Reference>
                    <ds:Reference Type="http://uri.etsi.org/01903#SignedProperties" URI="#SignedProperties-410721055">
                        <ds:DigestMethod Algorithm="http://www.w3.org/2001/04/xmlenc#sha512"/>
                        <ds:DigestValue>3LGSrBc+ONSGCu0MGDdM0hdd0kpwgmZ4gn3qSI8nmCdhcSdxm+6Vp7CZccT5Z/BMfZ+DZdcYrDhYk34SFC5wOw==</ds:DigestValue>
                    </ds:Reference>
                </ds:SignedInfo>
                <ds:SignatureValue Id="SignatureValue-1017221118">ma65UpnxSZKLXSC0WIuM3yqgbYm+PyfeUws3OHyxHBI5/4qHnl4CjTs2JnaMVO4j20R275RmAMwRyKrUTQ+gvOL0IR7ge7Xq+GXXbt2DyBe7FwCdseiGtqHa5P2Fir+eQrufNGFLAg3QrNI9drLhVBJBIoA6Lc60N+jGG8jovr02DQYfmyXsystMwrqF0AtwWzOy5c/jXhx/JDlqTYxeVBpHMcczVqmmROzp3PRpS4hWXP28JWBHnNTjwOjkiJYN6f74MgPuNT543jU82setXulyxKaFNvVfeDSaVxrR8TJQVPoYLM0V5W1IAvqO4B1uWicKFNK1xJXKrt25CQlByw==</ds:SignatureValue>
                <ds:KeyInfo>
                    <ds:X509Data>
                        <ds:X509Certificate>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</ds:X509Certificate>
                    </ds:X509Data>
                </ds:KeyInfo>
                <ds:Object>
                    <xades:QualifyingProperties xmlns:xades="http://uri.etsi.org/01903/v1.3.2#" xmlns:xadesv141="http://uri.etsi.org/01903/v1.4.1#" Target="#Signature-874039591">
                        <xades:SignedProperties Id="SignedProperties-410721055">
                            <xades:SignedSignatureProperties>
                                <xades:SigningTime>2017-12-13T08:02:15.760Z</xades:SigningTime>
                                <xades:SigningCertificate>
                                    <xades:Cert>
                                        <xades:CertDigest>
                                            <ds:DigestMethod Algorithm="http://www.w3.org/2001/04/xmlenc#sha512"/>
                                            <ds:DigestValue>qgFgBgYXMGPN9DCkxSyjgqfwxWdl8nlKoGuCFSI/wL2V6vNtVYlZVZO0g8BbTZeHyCr+M5Lszvp9QU3wDaZP9Q==</ds:DigestValue>
                                        </xades:CertDigest>
                                        <xades:IssuerSerial>
                                            <ds:X509IssuerName>2.5.4.5=#130E4E5452435A2D3236343339333935, O=PrvnÃ­ certifikaÄnÃ­ autorita\, a.s., CN=I.CA Test Qualified CA/RSA 11/2015, C=CZ</ds:X509IssuerName>
                                            <ds:X509SerialNumber>10060298</ds:X509SerialNumber>
                                        </xades:IssuerSerial>
                                    </xades:Cert>
                                </xades:SigningCertificate>
                            </xades:SignedSignatureProperties>
                            <xades:SignedDataObjectProperties>
                                <xades:DataObjectFormat ObjectReference="#envObjRef-3742974204">
                                    <xades:MimeType>text/xml</xades:MimeType>
                                </xades:DataObjectFormat>
                            </xades:SignedDataObjectProperties>
                        </xades:SignedProperties>
                        <UnsignedProperties xmlns="http://uri.etsi.org/01903/v1.3.2#">
                            <UnsignedSignatureProperties>
                                <SignatureTimeStamp Id="SignatureTimeStamp">
                                    <CanonicalizationMethod xmlns="http://www.w3.org/2000/09/xmldsig#" Algorithm="http://www.w3.org/TR/2001/REC-xml-c14n-20010315"/>
                                    <EncapsulatedTimeStamp Encoding="http://uri.etsi.org/01903/v1.2.2#DER">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</EncapsulatedTimeStamp>
                                </SignatureTimeStamp>
                            </UnsignedSignatureProperties>
                        </UnsignedProperties>
                    </xades:QualifyingProperties>
                </ds:Object>
                <ds:Object Id="envObj-2720751675">
                    <gu:ProtectedDocuments xmlns:gu="http://mmr.cz/wcc/v1.0.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" format="MMR-XML" version="1.0" xsi:schemaLocation="http://mmr.cz/wcc/v1.0.0 FormatDefinition_v1.0.xsd">
                        <ProtectedFile>
                            <Id>55033</Id>
                            <AltId>15OOTLIFPFIU6-E91OD2CEIG</AltId>
                            <Revison>46</Revison>
                            <FileName>ssss.xml</FileName>
                            <MimeType>application/xml</MimeType>
                            <Size>5821</Size>
                            <DigestType>SHA-512</DigestType>
                            <DigestValue>wJOhXRkEn3O0rNy1UuAWwQR8/4zz4QLyrOuI1VcMzfAfe09J7WJZfpmvoe4CZqRHTHM+MOCCkPuM0T8P/+vmtw==</DigestValue>
                        </ProtectedFile>
                    </gu:ProtectedDocuments>
                </ds:Object>
            </ds:Signature>`;

        //signatureProfile.settings.signatureType = "Enveloping";
        signatureProfile.settings.otherParams = { makeCounterSignaturesOnLeaves: false };

        //const stream = new StringSignatureStream(xmlData, "data.xml");

        //let [signedInfo] = await session.createDocumentSignatures(signatureProfile.settings, stream);
        let sign = xmlData;
        //sign = StringUtils.UTF8BytesToString(await new NativeAppStreamReader(signedInfo.stream).read());
        let [signedInfo] = await session.createCounterSignature(signatureProfile.settings, new StringSignatureStream(sign, "data.xml"));
        sign = StringUtils.UTF8BytesToString(await new NativeAppStreamReader(signedInfo.stream).read());
        [signedInfo] = await session.createCounterSignature(signatureProfile.settings, new StringSignatureStream(sign, "data.xml"));
        let signData = await new NativeAppStreamReader(signedInfo.stream).read();
        sign = StringUtils.UTF8BytesToString(signData);
    }*/
}

export interface IUploaderListItem {
    uploader: FileUploader;
    fileName: string;
}

/**
 * Interface pro vyhodnocení, zda je povolit podepisování.
 */
export interface ICanSignParams {
    actualObject: OWObject;
    attrName: string;
    control?: EditableControl;
    digitalSignatureState: DigitalSignatureState;
    isComponentEmpty: boolean;
    rightsCollection: IItemRightsCollection;
    allowCounterSignature: boolean;
    allowSignWithoutEdit: boolean;
    fileName: string;
}
